/**
 * Billing store — manages the current subscription state and billing actions.
 * Exposes actions that proxy to billingService and updates local state
 * using the confirm-then-update strategy (UX convention).
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Subscription, PlanTier, PlanCycle, Invoice } from '@/types/billing';
import type { QuoteConfig } from '@/types/pricingQuote';
import * as billing from '@/services/billingService';
import { fetchVipStatus } from '@/services/vipService';
import { captureEvent } from '@/boot/posthog';

export const useBillingStore = defineStore('billing', () => {
  // ── State ──────────────────────────────────────────────────────────────────

  const currentSubscription = ref<Subscription | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  /** Invoice list state. */
  const invoices = ref<Invoice[]>([]);
  const invoicesLoading = ref(false);
  const invoicesError = ref<string | null>(null);

  /**
   * VIP entitlement state (admin-vip-unlimited-access, D8). The guard is
   * the ACCOUNT the status was read for, never a bare boolean (review P2):
   * a boolean survives a sign-out and hands the next account signed in
   * within the same tab the previous one's badge, gauges and suppressed
   * trial banner. `null` means "unknown" — not "not unlimited" — so a
   * failed read stays retryable instead of pinning isVip=false for the
   * whole session. The status is per-account, not per-workspace, so it
   * still does not re-fetch on a workspace switch.
   */
  const isVip = ref(false);
  const vipLoadedFor = ref<string | null>(null);

  /**
   * Monotonic token invalidating in-flight VIP reads. Incremented by every
   * new load and by resetVipStatus, so a slow response for a previous
   * account can never land on the current one.
   */
  let vipRequestToken = 0;

  // ── Computed ───────────────────────────────────────────────────────────────

  /** Returns true when the user has an active paid plan (studio or team). */
  const hasActivePaidPlan = computed(() => {
    const sub = currentSubscription.value;
    if (!sub) return false;
    return (
      ['studio', 'team'].includes(sub.plan) &&
      ['active', 'past_due'].includes(sub.status)
    );
  });

  // ── Actions ────────────────────────────────────────────────────────────────

  /**
   * Fetches the current subscription for a given workspace.
   * @param workspaceId - The workspace ID to fetch.
   */
  const fetchSubscription = async (workspaceId: string): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const prev = currentSubscription.value;
      currentSubscription.value = await billing.fetchSubscription(workspaceId);
      // M11 — track trial_started when transitioning to trialing
      const next = currentSubscription.value;
      if (next?.status === 'trialing' && (!prev || prev.status !== 'trialing')) {
        captureEvent('trial_started', { plan: next.plan });
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Creates a Stripe Checkout session and redirects to Stripe.
   * If the user already has an active plan, redirects to the billing section.
   * @param tier - The target plan tier.
   * @param cycle - The billing cycle.
   * @param options - Optional flags (e.g. collectCardOnly for no-card trial flow D4).
   */
  const startCheckout = async (
    tier: Exclude<PlanTier, 'trial'>,
    cycle: PlanCycle,
    options?: { collectCardOnly?: boolean }
  ): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      captureEvent('checkout_session_created', { tier, cycle, collectCardOnly: options?.collectCardOnly });
      const result = await billing.startCheckout(tier, cycle, options);
      if (typeof window !== 'undefined') {
        window.location.href = result.url;
      }
    } catch (err) {
      if (
        err instanceof Error &&
        (err as Error & { code?: string }).code === 'already_subscribed'
      ) {
        const redirectTo = (err as Error & { redirectTo?: string }).redirectTo ?? '/app/account#billing';
        if (typeof window !== 'undefined') {
          window.location.href = redirectTo;
        }
        return;
      }
      error.value = err instanceof Error ? err.message : String(err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Opens the Stripe Customer Portal.
   */
  const openPortal = async (): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const result = await billing.openPortal();
      if (typeof window !== 'undefined') {
        window.location.href = result.url;
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Updates the subscription (tier, cycle, or both).
   * Confirm-then-update: only updates local state after server confirms (D8).
   * @param params - Target tier and/or cycle.
   */
  const updateSubscription = async (params: {
    targetTier?: Exclude<PlanTier, 'trial' | 'free'>;
    targetCycle?: PlanCycle;
  }): Promise<void> => {
    loading.value = true;
    error.value = null;
    const prevPlan = currentSubscription.value?.plan;
    const prevCycle = currentSubscription.value?.cycle;
    try {
      const updated = await billing.updateSubscription(params);
      // Patch local state after server confirms.
      if (currentSubscription.value) {
        currentSubscription.value = {
          ...currentSubscription.value,
          plan: updated.plan,
          cycle: updated.cycle,
          status: updated.status as Subscription['status'],
          currentPeriodEnd: updated.currentPeriodEnd
        };
      }
      captureEvent('plan_changed', {
        from: prevPlan,
        fromCycle: prevCycle,
        to: updated.plan,
        toCycle: updated.cycle
      });
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Updates the subscription to a new plan tier.
   * Rétro-compatible wrapper over updateSubscription (D8).
   * @param tier - The target plan tier.
   */
  const updatePlan = async (tier: Exclude<PlanTier, 'trial'>): Promise<void> => {
    return updateSubscription({ targetTier: tier as Exclude<PlanTier, 'trial' | 'free'> });
  };

  /**
   * Switches the billing cycle between monthly and yearly.
   * Convenience alias exposing the intent clearly at call sites.
   * @param targetCycle - The target billing cycle.
   */
  const switchCycle = async (targetCycle: PlanCycle): Promise<void> => {
    return updateSubscription({ targetCycle });
  };

  /**
   * Cancels the subscription at the end of the current period.
   * Confirm-then-update.
   */
  const cancelPlan = async (): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const result = await billing.cancelPlan();
      if (currentSubscription.value) {
        currentSubscription.value = {
          ...currentSubscription.value,
          cancelAtPeriodEnd: true,
          currentPeriodEnd: result.periodEnd
        };
      }
      // M11 — track plan_cancelled
      captureEvent('plan_cancelled', { plan: currentSubscription.value?.plan });
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetches the prorated upcoming invoice for a plan change (tier or cycle).
   * @param params - Target tier and/or cycle.
   */
  const fetchUpcomingInvoice = async (params: {
    targetTier?: Exclude<PlanTier, 'trial' | 'free'>;
    targetCycle?: PlanCycle;
  }): Promise<billing.UpcomingInvoiceResult> => {
    return billing.fetchUpcomingInvoice(params);
  };

  /**
   * Fetches the VIP entitlement status for the given user, once per account
   * (D8). A second call for the SAME account is a no-op — the status changes
   * at most a few times in an account's life, and this repo deliberately does
   * not open a realtime channel for it (D8: no `subscribeRealtime` reuse, no
   * new `.channel()`). A different account re-reads (review P2), and so does
   * a call following a failed read.
   *
   * Fails CLOSED and never memoises a failure: on `failed`, isVip stays false
   * and the guard stays `null` so the next call tries again.
   * @param userId - The `auth.users.id` to check.
   */
  const loadVipStatus = async (userId: string): Promise<void> => {
    if (!userId) return;
    if (vipLoadedFor.value === userId) return;
    const token = ++vipRequestToken;
    const status = await fetchVipStatus(userId);
    // A newer load, or a sign-out, superseded this read while it was in flight.
    if (token !== vipRequestToken) return;
    if (status.failed) {
      isVip.value = false;
      vipLoadedFor.value = null;
      return;
    }
    isVip.value = status.isVip;
    vipLoadedFor.value = userId;
  };

  /**
   * Drops the VIP entitlement (review P2). Called on every auth identity
   * change — sign-out included (`stores/auth.ts`, setSession) — because the
   * entitlement belongs to the account, not to the tab: without this, the
   * next account signed in inherits the badge, the "Unlimited" gauges and
   * the suppressed trial banner. Fails closed: the reset value is false.
   */
  const resetVipStatus = (): void => {
    vipRequestToken += 1;
    isVip.value = false;
    vipLoadedFor.value = null;
  };

  /**
   * Fetches the list of Stripe invoices for the current workspace.
   * @param workspaceId - Optional workspace ID (used for keying, not sent to edge fn).
   */
  const fetchInvoices = async (_workspaceId?: string): Promise<void> => {
    invoicesLoading.value = true;
    invoicesError.value = null;
    try {
      invoices.value = await billing.fetchInvoices();
    } catch (err) {
      invoicesError.value = err instanceof Error ? err.message : String(err);
    } finally {
      invoicesLoading.value = false;
    }
  };

  /**
   * Calls the configurator-checkout edge function in subscribe mode.
   * Returns the checkout URL and server-validated price. Captures PostHog event.
   * @param config - The configurator quota config.
   * @param locale - The user locale.
   */
  const startConfiguratorCheckout = async (
    config: QuoteConfig,
    locale: 'en' | 'fr'
  ): Promise<billing.ConfiguratorCheckoutResult> => {
    captureEvent('checkout_session_created', {
      source: 'configurator',
      locale
    });
    return billing.startConfiguratorCheckout(config, locale, 'subscribe');
  };

  /**
   * Calls the configurator-checkout edge function in update mode.
   * Returns the prorata amount for the confirmation modal.
   * Applies confirm-then-update: subscription state is refreshed by webhook / realtime.
   * @param config - The updated configurator quota config.
   * @param locale - The user locale.
   */
  const updateConfiguratorPlan = async (
    config: QuoteConfig,
    locale: 'en' | 'fr'
  ): Promise<billing.ConfiguratorCheckoutResult> => {
    captureEvent('plan_changed', {
      source: 'configurator',
      locale
    });
    return billing.startConfiguratorCheckout(config, locale, 'update');
  };

  return {
    // State
    currentSubscription,
    loading,
    error,
    invoices,
    invoicesLoading,
    invoicesError,
    isVip,
    vipLoadedFor,
    // Computed
    hasActivePaidPlan,
    // Actions
    fetchSubscription,
    startCheckout,
    openPortal,
    updateSubscription,
    updatePlan,
    switchCycle,
    cancelPlan,
    fetchUpcomingInvoice,
    fetchInvoices,
    startConfiguratorCheckout,
    updateConfiguratorPlan,
    loadVipStatus,
    resetVipStatus
  };
});
